import { useState, useEffect, useRef, forwardRef, useMemo } from "react";
import {
  User,
  FileText,
  Shield,
  ChevronDown,
  Search,
  Eye,
  EyeOff,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Images from "../../../utils/Images";
import { Link, useNavigate, useLocation } from "react-router-dom";
import regions from "../../../data/Regions";
import locations from "../../../data/Locations";
import authService from "../../../api/AuthService";
import { toast } from "react-toastify";
import { setNewUser } from "../../../store/slices/authSlice";
import { useDispatch } from "react-redux";
import { completeSplash } from "../../../store/slices/onboardingSlice";
import { validateInternationalPhone } from "../../../utils/formValidators";

const capitalizeCountryForPayload = (country) => {
  if (!country) return "";
  const normalized = String(country).trim().toLowerCase();
  if (!normalized) return "";

  // Title Case each word (e.g., "united states" -> "United States")
  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isGhanaCountry = (country) => {
  if (!country) return false;
  return String(country).trim().toLowerCase() === "ghana";
};

const normalizePhoneForPayload = (phone) => {
  if (!phone) return "";
  // Keep country code digits, drop leading "+" and any formatting chars.
  return String(phone).replace(/^\+/, "").replace(/\D/g, "");
};

export default function SignUp() {
  const dispatch = useDispatch();

  // Add mobile viewport height fix
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);

    return () => {
      window.removeEventListener("resize", setVH);
      window.removeEventListener("orientationchange", setVH);
    };
  }, []);
  const [currentStep, setCurrentStep] = useState(1);
  const progressRef = useRef(null);
  const nodeRefs = useRef([]);
  const [railMetrics, setRailMetrics] = useState({ left: 0, width: 0 });
  const [formData, setFormData] = useState({
    // Step 1: Personal & Contact
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    phoneNumber: "",
    gender: "",
    dob: "",
    region: "",
    location: "",
    customLocation: "",
    // Step 3: Security Setup
    password: "",
    confirmPassword: "",
    acceptedTerms: false,
    // Step 2: ID Upload (front/back)
    id_card_front: null,
    idFrontPreview: "",
    id_card_back: null,
    idBackPreview: "",
    // Step 2: Document details
    document_type: "",
    card_number: "",
    issue_date: "",
    expiry_date: "",
  });

  const isGhana = isGhanaCountry(formData.country);
  const prevIsGhanaRef = useRef(isGhana);

  useEffect(() => {
    // When the user switches between Ghana and non-Ghana, reset location fields
    // so the UI mode and the payload stay consistent.
    if (prevIsGhanaRef.current === isGhana) return;
    prevIsGhanaRef.current = isGhana;
    setFormData((prev) => ({
      ...prev,
      region: "",
      location: "",
      customLocation: "",
    }));
  }, [isGhana]);

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from;

  const steps = [
    {
      id: 1,
      title: "Personal & Contact",
      subtitle: "Basic Information",
      icon: User,
      completed: currentStep > 1,
    },
    {
      id: 2,
      title: "Document Upload",
      subtitle: "Basic Information",
      icon: FileText,
      completed: currentStep > 2,
    },
    {
      id: 3,
      title: "Security Setup",
      subtitle: "Secure your account",
      icon: Shield,
      completed: currentStep > 3,
    },
  ];

  useEffect(() => {
    const updateRailMetrics = () => {
      if (!progressRef.current || nodeRefs.current.length === 0) return;
      const containerRect = progressRef.current.getBoundingClientRect();
      const firstNode = nodeRefs.current[0];
      const lastNode = nodeRefs.current[nodeRefs.current.length - 1];
      if (!firstNode || !lastNode) return;
      const firstRect = firstNode.getBoundingClientRect();
      const lastRect = lastNode.getBoundingClientRect();
      const firstCenter = firstRect.left + firstRect.width / 2;
      const lastCenter = lastRect.left + lastRect.width / 2;
      const left = Math.max(0, firstCenter - containerRect.left);
      const width = Math.max(0, lastCenter - firstCenter);
      setRailMetrics({ left, width });
    };

    updateRailMetrics();
    window.addEventListener("resize", updateRailMetrics);
    return () => window.removeEventListener("resize", updateRailMetrics);
  }, [steps.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    
    // When document_type is cleared, clear all document-related validation errors
    if (field === "document_type" && !value) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.document_type;
        delete newErrors.card_number;
        delete newErrors.issue_date;
        delete newErrors.expiry_date;
        delete newErrors.id_card_front;
        delete newErrors.id_card_back;
        return newErrors;
      });
    }
  };

  const getFieldError = (field) => {
    return validationErrors[field]?.[0];
  };

  const getStepErrors = (errorFields) => {
    // Map API field names to form field names for step validation
    const fieldToStepMap = {
      // Step 1: Personal & Contact
      first_name: 1,
      last_name: 1,
      email: 1,
      country: 1,
      phone_number: 1,
      gender: 1,
      dob: 1,
      region: 1,
      location: 1,

      // Step 2: Document Upload
      document_type: 2,
      card_number: 2,
      issue_date: 2,
      expiry_date: 2,
      id_card_front: 2,
      id_card_back: 2,

      // Step 3: Security Setup
      password: 3,
      password_confirmation: 3,
    };

    return errorFields.filter((field) => fieldToStepMap[field] === currentStep);
  };

  const getCurrentStepFields = (step) => {
    // Return the API field names for the current step
    const stepFieldMap = {
      1: [
        "first_name",
        "last_name",
        "email",
        "country",
        "phone_number",
        "gender",
        "dob",
        "region",
        "location",
      ],
      2: [
        "document_type",
        "card_number",
        "issue_date",
        "expiry_date",
        "id_card_front",
        "id_card_back",
      ],
      3: ["password", "password_confirmation"],
    };

    return stepFieldMap[step] || [];
  };

  const convertImgToBase64 = (file, field) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setFormData((prev) => ({ ...prev, [field]: base64String }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error converting image to base64:", error);
      return null;
    }
  };

  const handleNextStep = async () => {
    if (currentStep < 3) {
      // For step 2 (Document Upload), allow skipping - only check errors if document type is selected
      if (currentStep === 2) {
        // If document type is selected, validate all required document fields
        if (formData.document_type) {
          // Validate all required fields
          const errors = {};
          
          // Card number validation
          if (!formData.card_number) {
            errors.card_number = ["Card number is required"];
          } else if (formData.document_type === "Ghana Card") {
            const cleanedCardNumber = formData.card_number.replace(/-/g, "");
            const ghanaCardPattern = /^GHA\d{10}$/i;
            if (!ghanaCardPattern.test(cleanedCardNumber)) {
              errors.card_number = ["Invalid Ghana Card number format"];
            }
          }
          
          // Document images validation
          if (!formData.id_card_front) {
            errors.id_card_front = ["Front document image is required"];
          }
          if (!formData.id_card_back) {
            errors.id_card_back = ["Back document image is required"];
          }
          
          // Date fields validation
          if (!formData.issue_date) {
            errors.issue_date = ["Issue date is required"];
          }
          if (!formData.expiry_date) {
            errors.expiry_date = ["Expiry date is required"];
          }
          
          // If there are validation errors, set them and prevent progression
          if (Object.keys(errors).length > 0) {
            setValidationErrors((prev) => ({ ...prev, ...errors }));
            const currentStepName = steps[currentStep - 1].title;
            toast.error(
              `Please complete all required document fields in ${currentStepName} step before continuing.`
            );
            return;
          }
          
          // Check for existing validation errors
          const currentStepFields = getCurrentStepFields(currentStep);
          const hasStepErrors = currentStepFields.some(
            (field) => validationErrors[field]
          );

          if (hasStepErrors) {
            const currentStepName = steps[currentStep - 1].title;
            toast.error(
              `Please fix the errors in ${currentStepName} step before continuing.`
            );
            return;
          }
        } else {
          // If no document type is selected, clear any document-related validation errors
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.document_type;
            delete newErrors.card_number;
            delete newErrors.issue_date;
            delete newErrors.expiry_date;
            delete newErrors.id_card_front;
            delete newErrors.id_card_back;
            return newErrors;
          });
        }
        // Allow progression (either with valid documents or skipping)
      } else {
        // For other steps, check validation errors normally
        const currentStepFields = getCurrentStepFields(currentStep);
        const hasStepErrors = currentStepFields.some(
          (field) => validationErrors[field]
        );

        if (hasStepErrors) {
          const currentStepName = steps[currentStep - 1].title;
          toast.error(
            `Please fix the errors in ${currentStepName} step before continuing.`
          );
          return;
        }
      }

      setCurrentStep(currentStep + 1);
      setValidationErrors({});

      // Show step completion feedback
      const completedStepName = steps[currentStep - 1].title;
      const nextStepName = steps[currentStep].title;
      toast.success(
        `✅ ${completedStepName} completed! Moving to ${nextStepName} step`
      );
    } else {
      const step1Status = getStepCompletionStatus(1);

      // Step 2 is optional, so only check step 1 completion
      if (!step1Status.completed) {
        toast.error(
          "Please complete all required steps before submitting the form."
        );
        return;
      }

      setIsSubmitting(true);

      const cleanedFormData = {
        ...formData,
        card_number:
          formData.document_type === "Ghana Card"
            ? formData.card_number.replace(/-/g, "")
            : formData.card_number,
      };

      const formatDOB = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}${month}${year}`;
      };

      const apiPayload = {
        first_name: cleanedFormData.firstName,
        last_name: cleanedFormData.lastName,
        email: cleanedFormData.email,
        phone_number: normalizePhoneForPayload(cleanedFormData.phoneNumber),
        password: cleanedFormData.password,
        password_confirmation: cleanedFormData.confirmPassword,
        country: capitalizeCountryForPayload(cleanedFormData.country),
        location: isGhana
          ? cleanedFormData.location === "Other"
            ? cleanedFormData.customLocation
            : cleanedFormData.location
          : cleanedFormData.location,
        dob: formatDOB(cleanedFormData.dob),
        region: cleanedFormData.region,
        gender: cleanedFormData.gender.toLowerCase(),
        document_type: cleanedFormData.document_type
          ? (() => {
              const mapping = {
                "Ghana Card": "ghana_card",
                Passport: "passport",
                "Drivers License": "drivers_license",
              };
              return mapping[cleanedFormData.document_type] || "";
            })()
          : "",
        card_number: cleanedFormData.card_number,
        id_card_front: cleanedFormData.id_card_front || "",
        id_card_back: cleanedFormData.id_card_back || "",
        issue_date: cleanedFormData.issue_date || "",
        expiry_date: cleanedFormData.expiry_date || "",
      };
      const response = await authService.register(apiPayload);
      const data = await response?.data?.data;

      if (data && data.status_code === 200) {
        toast.success(data.reason || "Registration successful!");
        dispatch(setNewUser(data.data));
        dispatch(completeSplash());
        setValidationErrors({});
        setIsSubmitting(false);
        navigate("/verify-account", {
          state: {
            consumer_slug: data?.data?.slug,
            phone_number: data?.data?.phone_number,
            email: data?.data?.email,
            from: redirectPath, // Pass the redirect path through to verification
          },
        });
      } else {
        // Show error toast
        toast.error(
          response.message || "Registration failed. Please try again."
        );

        // If there are validation errors, show them in the UI
        if (response.validationErrors) {
          console.log("Validation errors:", response.validationErrors);
          setValidationErrors(response.validationErrors);

          // Show step-specific validation error toast
          const errorFields = Object.keys(response.validationErrors);
          const stepErrors = getStepErrors(errorFields);

          if (stepErrors.length > 0) {
            const currentStepName = steps[currentStep - 1].title;
            toast.error(
              `Please fix the errors in ${currentStepName} step before continuing.`
            );
          }
        } else {
          setValidationErrors({});
        }
      }
      setIsSubmitting(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({});
      toast.info(`Back to ${steps[currentStep - 2].title} step`);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        const phoneValidation = validateInternationalPhone(
          formData.phoneNumber
        );
        const phoneValid = phoneValidation.isValid;
        const countryValid = !!formData.country && !!formData.country.trim();

        const locationValid = isGhana
          ? formData.location ||
            (formData.location === "Other" && formData.customLocation)
          : !!formData.location && !!formData.location.trim();

        const dobValid = (() => {
          if (!formData.dob) return false;
          const dob = new Date(formData.dob);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          ) {
            return age - 1 >= 18;
          }
          return age >= 18;
        })();

        return (
          formData.firstName &&
          formData.lastName &&
          formData.email &&
          phoneValid &&
          countryValid &&
          formData.gender &&
          dobValid &&
          formData.region &&
          locationValid
        );
      case 2:
        // If document type is selected, all document fields become required
        if (formData.document_type) {
          // Validate Ghana Card number format if Ghana Card is selected
          if (formData.document_type === "Ghana Card") {
            if (!formData.card_number) return false;
            const cleanedCardNumber = formData.card_number.replace(/-/g, "");
            const ghanaCardPattern = /^GHA\d{10}$/i;
            if (!ghanaCardPattern.test(cleanedCardNumber)) return false;
          } else {
            // For other document types, card_number is still required
            if (!formData.card_number) return false;
          }
          
          // All document fields are required when document_type is selected
          if (!formData.id_card_front || !formData.id_card_back) return false;
          if (!formData.issue_date || !formData.expiry_date) return false;
          
          return true;
        }
        // If no document type is selected, step is valid (optional)
        return true;
      case 3:
        if (
          !formData.password ||
          !formData.confirmPassword ||
          formData.password !== formData.confirmPassword ||
          !formData.acceptedTerms
        ) {
          return false;
        }

        const password = formData.password;
        const hasLength = password.length >= 8;
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const criteriaMet = [
          hasLength,
          hasLowercase,
          hasUppercase,
          hasNumbers,
          hasSymbols,
        ].filter(Boolean).length;

        return criteriaMet >= 4;
      default:
        return false;
    }
  };

  const getStepCompletionStatus = (step) => {
    switch (step) {
      case 1:
        const phoneValid = validateInternationalPhone(formData.phoneNumber)
          .isValid;
        const countryValid = !!formData.country && !!formData.country.trim();

        const locationValid = isGhana
          ? formData.location ||
            (formData.location === "Other" && formData.customLocation)
          : !!formData.location && !!formData.location.trim();

        const dobValid = (() => {
          if (!formData.dob) return false;
          const dob = new Date(formData.dob);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          ) {
            return age - 1 >= 18;
          }
          return age >= 18;
        })();

        return {
          completed:
            formData.firstName &&
            formData.lastName &&
            formData.email &&
            phoneValid &&
            countryValid &&
            formData.gender &&
            dobValid &&
            formData.region &&
            locationValid,
          progress: [
            { field: "firstName", valid: !!formData.firstName },
            { field: "lastName", valid: !!formData.lastName },
            { field: "email", valid: !!formData.email },
            { field: "phoneNumber", valid: phoneValid },
            { field: "country", valid: countryValid },
            { field: "gender", valid: !!formData.gender },
            { field: "dob", valid: dobValid },
            { field: "region", valid: !!formData.region },
            { field: "location", valid: locationValid },
          ].filter((item) => item.valid).length,
        };
      case 2:
        // If document type is selected, all fields must be completed
        if (formData.document_type) {
          let isValid = true;
          
          // Validate Ghana Card number format if Ghana Card is selected
          if (formData.document_type === "Ghana Card") {
            if (!formData.card_number) {
              isValid = false;
            } else {
              const cleanedCardNumber = formData.card_number.replace(/-/g, "");
              const ghanaCardPattern = /^GHA\d{10}$/i;
              if (!ghanaCardPattern.test(cleanedCardNumber)) {
                isValid = false;
              }
            }
          } else {
            // For other document types, card_number is still required
            if (!formData.card_number) {
              isValid = false;
            }
          }
          
          // All document fields must be present
          if (!formData.id_card_front || !formData.id_card_back) {
            isValid = false;
          }
          if (!formData.issue_date || !formData.expiry_date) {
            isValid = false;
          }
          
          return {
            completed: isValid,
            progress: isValid ? 1 : 0.5,
          };
        }
        
        // If no document type is selected, step is considered complete (optional)
        return {
          completed: true,
          progress: 0,
        };
      case 3:
        const passwordValid =
          formData.password &&
          formData.confirmPassword &&
          formData.password === formData.confirmPassword;
        const passwordStrength = formData.password
          ? [
              formData.password.length >= 8,
              /[a-z]/.test(formData.password),
              /[A-Z]/.test(formData.password),
              /\d/.test(formData.password),
              /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
            ].filter(Boolean).length
          : 0;

        return {
          completed: passwordValid && passwordStrength >= 4 && formData.acceptedTerms,
          progress: passwordStrength,
        };
      default:
        return { completed: false, progress: 0 };
    }
  };

  return (
    <div
      className="grid relative grid-cols-1 grid-rows-1 h-screen max-h-screen overflow-hidden lg:grid-cols-2 lg:h-screen lg:overflow-hidden"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100)" }}
    >
      {/* Loading Overlay */}
      {isSubmitting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/20"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col gap-4 items-center p-8 bg-white rounded-2xl shadow-2xl"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 rounded-full border-4 border-emerald-500 border-t-transparent"
            />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Creating Your Account
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Please wait while we process your registration...
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
      {/* Left Hero Panel */}
      <motion.div
        className="hidden relative w-full h-full lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src={`https://images.unsplash.com/photo-1560264418-c4445382edbc?q=80&w=1470&auto=format&fit=crop`}
          alt="trip-secure-signin"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30" />
        <div className="flex absolute inset-0 flex-col justify-between p-12">
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.8,
              type: "spring",
              bounce: 0.4,
            }}
            className="text-white"
          >
            <motion.h3
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-3 text-2xl font-light"
            >
              Welcome to
            </motion.h3>
            <motion.h2
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-4xl font-bold bg-gradient-to-r from-[#FF6C6C] to-white bg-clip-text text-transparent"
            >
              <Link to="/" className="block cursor-pointer group">
                <img
                  src={Images.logo_navbar}
                  alt="trip-secure"
                  className="h-10 transition-all duration-200 hover:opacity-80 group-hover:scale-105"
                />
              </Link>
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="space-y-6"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="p-6 bg-gradient-to-r rounded-xl border backdrop-blur-md from-black/40 to-black/20 border-white/10"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="mb-4 text-lg italic text-white/90"
              >
                "Travel far, explore freely, and leave the worries behind—our
                trip insurance has you covered every step of the way."
              </motion.p>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
                className="flex gap-4 items-center"
              >
                <div className="flex -space-x-3">
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.4 + i * 0.2 }}
                      className="w-10 h-10 rounded-full border-2 backdrop-blur-sm border-[#05E881]/20 bg-black/30"
                    />
                  ))}
                </div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                  className="text-sm text-white/80"
                >
                  Join our{" "}
                  <span className="text-[#05E881] font-bold">100+</span> members
                </motion.p>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.2,
                  },
                },
              }}
              className="grid grid-cols-3 gap-4"
            >
              {[
                { number: "100,000k+", label: "Policies Issued" },
                { number: "300+", label: "Registered Insurance Providers" },
                { number: "GHS 1 million+", label: "in claims paid" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  }}
                  className="p-4 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-black/30 border-white/10"
                >
                  <motion.h4
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2 + index * 0.2 }}
                    className="mb-1 text-xl font-bold text-[#05E881]"
                  >
                    {stat.number}
                  </motion.h4>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2 + index * 0.2 }}
                    className="text-sm text-white/70"
                  >
                    {stat.label}
                  </motion.p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Form Panel */}
      <div
        className="flex min-h-0 h-full overflow-y-auto justify-center items-start p-6 lg:p-12 lg:h-screen lg:overflow-y-auto"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
      >
        <div className="pb-8 w-full max-w-xl lg:pb-0 form-container">
          {/* Logo for mobile */}
          <Link to="/" className="block cursor-pointer group">
            <div className="flex justify-center p-2 mb-6 rounded-lg transition-colors duration-200 lg:hidden hover:bg-gray-50">
              <img
                src={Images.logo_colored}
                alt="TripSecure"
                className="h-16 transition-all duration-200 hover:opacity-80 group-hover:scale-105"
              />
            </div>
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl font-bold text-center">Create Account</h1>
            <p className="mt-1 text-sm text-center text-gray-600">
              Join us to Secure Your Journey—Get Covered in Minutes
            </p>
          </motion.div>

          {/* Overall Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6"
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Overall Progress
              </span>
              <span className="text-sm font-medium text-emerald-600">
                {(() => {
                  const totalSteps = 3;
                  const completedSteps = [1, 2, 3].filter(
                    (step) => getStepCompletionStatus(step).completed
                  ).length;
                  return `${completedSteps}/${totalSteps} steps completed`;
                })()}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-emerald-500 rounded-full transition-all duration-500"
                style={{
                  width: `${(() => {
                    const totalSteps = 3;
                    const completedSteps = [1, 2, 3].filter(
                      (step) => getStepCompletionStatus(step).completed
                    ).length;
                    return (completedSteps / totalSteps) * 100;
                  })()}%`,
                }}
              />
            </div>
          </motion.div>

          {/* Step Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative mt-8 mb-8"
            ref={progressRef}
          >
            {/* Rails constrained between node centers */}
            <div
              className="absolute top-5"
              style={{ left: railMetrics.left, width: railMetrics.width }}
            >
              <div className="h-0.5 bg-gray-200" />
              <div
                className="h-0.5 bg-emerald-500 transition-all duration-500 absolute left-0 top-0"
                style={{
                  width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                }}
              />
            </div>
            <div className="flex relative justify-between items-center">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className="flex flex-col flex-1 items-center"
                >
                  <div
                    className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                      currentStep === step.id
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : step.completed
                        ? "bg-emerald-100 border-emerald-500 text-emerald-600"
                        : "bg-gray-100 border-gray-300 text-gray-400"
                    }`}
                    ref={(el) => (nodeRefs.current[index] = el)}
                  >
                    {step.completed ? (
                      <svg
                        className="w-5 h-5 text-emerald-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <step.icon size={16} />
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div
                      className={`text-xs font-medium ${
                        currentStep === step.id || step.completed
                          ? "text-emerald-600"
                          : "text-gray-400"
                      }`}
                    >
                      {step.title}
                    </div>
                    {/* <div className="text-xs text-gray-500">{step.subtitle}</div> */}
                    {step.completed && (
                      <div className="mt-1 text-xs font-medium text-emerald-500">
                        ✓ Completed
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {currentStep === 1 && (
                <PersonalContactStep
                  formData={formData}
                  onChange={handleInputChange}
                  getFieldError={getFieldError}
                />
              )}
              {currentStep === 2 && (
                <DocumentUploadStep
                  formData={formData}
                  onChange={handleInputChange}
                  convertImgToBase64={convertImgToBase64}
                  onNextStep={() => {
                    // Clear any document-related validation errors when skipping
                    setValidationErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.document_type;
                      delete newErrors.card_number;
                      delete newErrors.issue_date;
                      delete newErrors.expiry_date;
                      delete newErrors.id_card_front;
                      delete newErrors.id_card_back;
                      return newErrors;
                    });
                    handleNextStep();
                  }}
                  getFieldError={getFieldError}
                  isSubmitting={isSubmitting}
                />
              )}
              {currentStep === 3 && (
                <SecuritySetupStep
                  formData={formData}
                  onChange={handleInputChange}
                  getFieldError={getFieldError}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="sticky bottom-0 py-4 mt-6 space-y-4 backdrop-blur-sm bg-white/95 lg:static lg:bg-transparent lg:py-0"
          >
            <motion.button
              onClick={handleNextStep}
              disabled={!isStepValid() || isSubmitting}
              whileHover={{ scale: isStepValid() && !isSubmitting ? 1.02 : 1 }}
              whileTap={{ scale: isStepValid() && !isSubmitting ? 0.98 : 1 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                    />
                    Creating Account...
                  </>
                ) : (
                  <>{currentStep === 3 ? "Create Account" : "Continue"}</>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>

            {currentStep > 1 && (
              <motion.button
                onClick={handlePrevStep}
                disabled={isSubmitting}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`px-4 py-3 w-full text-sm font-medium rounded-xl transition-colors ${
                  isSubmitting
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {isSubmitting ? "Please wait..." : "Back"}
              </motion.button>
            )}

            <p className="text-xs text-center text-gray-600">
              Already have an account?{" "}
              <Link
                to="/signin"
                className="text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Login
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// Step 1: Personal & Contact Information
function PersonalContactStep({ formData, onChange, getFieldError }) {
  const phoneValidation = validateInternationalPhone(formData.phoneNumber);
  const isGhana = isGhanaCountry(formData.country);

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <h3 className="text-lg font-semibold">
          Personal & Contact Information
        </h3>
        <p className="text-xs text-gray-500">
          Let's start with your basic information and how we can reach you
        </p>
      </motion.div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <InputField
            type="text"
            placeholder="First Name"
            value={formData.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
            delay={0.2}
            error={getFieldError("first_name")}
          />
          <InputField
            type="text"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            delay={0.25}
            error={getFieldError("last_name")}
          />
        </div>

        <InputField
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => onChange("email", e.target.value)}
          delay={0.3}
          error={getFieldError("email")}
        />

        <InputField
          type="text"
          placeholder="Country"
          value={formData.country}
          onChange={(e) => onChange("country", e.target.value)}
          delay={0.32}
          error={getFieldError("country")}
        />

        <PhoneNumberField
          value={formData.phoneNumber}
          onChange={(value) => onChange("phoneNumber", value)}
          delay={0.35}
          error={getFieldError("phone_number")}
        />

        {/* Debug display - remove this after testing */}
        {formData.phoneNumber && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="p-3 text-xs bg-blue-50 rounded-lg border border-blue-200"
          >
            <p className="font-medium text-blue-800">
              Phone Number Validation:
            </p>
            <p className="font-mono text-blue-600">{formData.phoneNumber}</p>
            <p className="mt-1 text-blue-500">
              {phoneValidation.isValid ? "✅ Valid format" : `❌ ${phoneValidation.message}`}
            </p>
          </motion.div>
        )}

        {/* Gender Radio Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="space-y-3"
        >
          <div className="text-sm font-medium text-gray-700">Gender</div>
          <div className="flex gap-3">
            {["Male", "Female"].map((option) => (
              <motion.label
                key={option}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-3 px-4 py-3 flex-1 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                  formData.gender === option
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() => onChange("gender", option)}
              >
                <div
                  className={`relative w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                    formData.gender === option
                      ? "border-emerald-500 bg-emerald-500"
                      : "border-gray-300"
                  }`}
                >
                  {formData.gender === option && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute inset-0.5 rounded-full bg-white"
                    />
                  )}
                </div>
                <span
                  className={`text-sm font-medium transition-colors ${
                    formData.gender === option
                      ? "text-emerald-700"
                      : "text-gray-700"
                  }`}
                >
                  {option}
                </span>
              </motion.label>
            ))}
          </div>
          {getFieldError("gender") && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs text-red-600"
            >
              {getFieldError("gender")}
            </motion.p>
          )}
        </motion.div>

        {/* Date of Birth */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="space-y-3"
        >
          <div>
            <InputField
              type="date"
              placeholder="Date of Birth"
              value={formData.dob}
              onChange={(e) => onChange("dob", e.target.value)}
              delay={0}
              error={getFieldError("dob")}
            />
            <div className="flex items-center gap-2 mt-2 px-3 py-2 text-xs bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                <span className="font-medium text-blue-700">
                  Select your birth date
                </span>
              </div>
              <div className="w-px h-3 bg-blue-200"></div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400"></div>
                <span className="font-mono text-indigo-600">
                  Must be 18+ years old
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Date of Birth helper text */}
        {formData.dob && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            className="text-xs text-gray-500"
          >
            {(() => {
              const dob = new Date(formData.dob);
              const today = new Date();
              const age = today.getFullYear() - dob.getFullYear();
              const monthDiff = today.getMonth() - dob.getMonth();

              if (
                monthDiff < 0 ||
                (monthDiff === 0 && today.getDate() < dob.getDate())
              ) {
                const actualAge = age - 1;
                return actualAge >= 18
                  ? `✅ Age: ${actualAge} years (Valid)`
                  : `❌ Age: ${actualAge} years (Must be at least 18)`;
              }
              return age >= 18
                ? `✅ Age: ${age} years (Valid)`
                : `❌ Age: ${age} years (Must be at least 18)`;
            })()}
          </motion.div>
        )}

        {isGhana ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                placeholder="Region"
                value={formData.region}
                onChange={(value) => {
                  onChange("region", value);
                  onChange("location", "");
                  onChange("customLocation", "");
                }}
                options={regions}
                delay={0.5}
                searchable={true}
                error={getFieldError("region")}
              />
              <SelectField
                placeholder={
                  formData.region ? "Select location" : "Region First"
                }
                value={formData.location}
                onChange={(value) => onChange("location", value)}
                options={
                  formData.region ? locations[formData.region] || [] : []
                }
                delay={0.55}
                searchable={true}
                disabled={!formData.region}
                error={getFieldError("location")}
              />
            </div>

            {formData.location === "Other" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
              >
                <InputField
                  type="text"
                  placeholder="Enter your location"
                  value={formData.customLocation}
                  onChange={(e) => onChange("customLocation", e.target.value)}
                  delay={0.65}
                  error={getFieldError("location")}
                />
              </motion.div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <InputField
              type="text"
              placeholder="State/Province"
              value={formData.region}
              onChange={(e) => onChange("region", e.target.value)}
              delay={0.5}
              error={getFieldError("region")}
            />
            <InputField
              type="text"
              placeholder="City/Town"
              value={formData.location}
              onChange={(e) => onChange("location", e.target.value)}
              delay={0.55}
              error={getFieldError("location")}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Step 2: Document Upload
function DocumentUploadStep({
  formData,
  onChange,
  convertImgToBase64,
  onNextStep,
  getFieldError,
  isSubmitting,
}) {
  // Function to format Ghana Card number as user types
  const formatGhanaCardNumber = (value) => {
    if (!value) return value;

    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "");

    // If it starts with GHA, format it
    if (cleaned.toUpperCase().startsWith("GHA")) {
      const ghaPart = cleaned.substring(0, 3).toUpperCase();
      const numbers = cleaned.substring(3);

      if (numbers.length <= 10) {
        if (numbers.length <= 9) {
          return `${ghaPart}-${numbers}`;
        } else if (numbers.length === 10) {
          return `${ghaPart}-${numbers.substring(0, 9)}-${numbers.substring(
            9
          )}`;
        }
      }
    }

    // If it doesn't start with GHA, just return the cleaned value
    return cleaned;
  };

  const handleFile = async (file, side) => {
    if (!file) return;
    const preview = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
    if (side === "front") {
      convertImgToBase64(file, "id_card_front");
      onChange("idFrontPreview", preview);
    } else {
      convertImgToBase64(file, "id_card_back");
      onChange("idBackPreview", preview);
    }
  };
  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <h3 className="text-lg font-semibold">Document Upload (Optional)</h3>
        <p className="text-xs text-gray-500">
          Upload your identification documents to verify your identity
        </p>
      </motion.div>

      {/* Document meta */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Document Type */}
        <div>
          <SelectField
            placeholder="Document Type (Optional)"
            value={formData.document_type}
            onChange={(v) => {
              onChange("document_type", v);
              onChange("card_number", "");
              onChange("issue_date", "");
              onChange("expiry_date", "");
              onChange("idFrontPreview", "");
              onChange("idBackPreview", "");
              onChange("id_card_front", null);
              onChange("id_card_back", null);
            }}
            options={["Ghana Card", "Passport"]}
            delay={0.3}
            allowClear={true}
            onClear={() => {
              onChange("document_type", "");
              onChange("card_number", "");
              onChange("issue_date", "");
              onChange("expiry_date", "");
              onChange("idFrontPreview", "");
              onChange("idBackPreview", "");
              onChange("id_card_front", null);
              onChange("id_card_back", null);
            }}
            error={getFieldError("document_type")}
          />
        </div>

        {/* Card Number - only active when document type is selected */}
        <div>
          <InputField
            type="text"
            placeholder={
              formData.document_type === "Ghana Card"
                ? "GHA-XXXXXXXXX-X"
                : "Card Number"
            }
            value={formData.card_number}
            onChange={(e) => {
              const value = e.target.value;
              // Apply Ghana Card formatting if Ghana Card is selected
              if (formData.document_type === "Ghana Card") {
                const formatted = formatGhanaCardNumber(value);
                onChange("card_number", formatted);
              } else {
                onChange("card_number", value);
              }
            }}
            onPaste={(e) => {
              if (formData.document_type === "Ghana Card") {
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");
                const formatted = formatGhanaCardNumber(pastedText);
                onChange("card_number", formatted);
              }
            }}
            delay={0.35}
            disabled={!formData.document_type}
            error={getFieldError("card_number")}
          />
          {/* Helper text for Ghana Card format */}
          {formData.document_type === "Ghana Card" && (
            <motion.p
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              className="mt-1 text-xs text-gray-500"
            >
              Format: GHA-XXXXXXXXX-X (e.g., GHA-123456789-0)
            </motion.p>
          )}
          {/* Validation message for Ghana Card */}
          {formData.document_type === "Ghana Card" && formData.card_number && (
            <motion.p
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.3 }}
              className={`mt-1 text-xs ${
                (() => {
                  const cleanedCardNumber = formData.card_number.replace(
                    /-/g,
                    ""
                  );
                  const ghanaCardPattern = /^GHA\d{10}$/i;
                  return ghanaCardPattern.test(cleanedCardNumber);
                })()
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {(() => {
                const cleanedCardNumber = formData.card_number.replace(
                  /-/g,
                  ""
                );
                const ghanaCardPattern = /^GHA\d{10}$/i;
                return ghanaCardPattern.test(cleanedCardNumber)
                  ? "✅ Valid Ghana Card format"
                  : "❌ Invalid Ghana Card format";
              })()}
            </motion.p>
          )}
        </div>
      </div>

      {/* Helper message when no document type is selected */}
      {!formData.document_type && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="p-3 text-xs bg-blue-50 rounded-lg border border-blue-200"
        >
          <p className="font-medium text-blue-800">
            📋 Document Upload (Optional)
          </p>
          <p className="mt-1 text-blue-600">
            You can select a document type to upload your ID documents, or skip
            this step and continue. Document upload helps verify your identity
            but is not required for account creation.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={onNextStep}
              disabled={isSubmitting}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                isSubmitting
                  ? "text-emerald-400 bg-emerald-50 border-emerald-200 cursor-not-allowed"
                  : "text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
              }`}
            >
              {isSubmitting ? "Please wait..." : "Continue without documents"}
            </button>
          </div>
        </motion.div>
      )}
      {/* Document Upload Fields - only active when document type is selected */}
      {formData.document_type && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {/* Front */}
          <motion.label
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex overflow-hidden relative flex-col justify-center items-center p-4 text-center rounded-xl border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-emerald-400 bg-white/80"
          >
            {formData.idFrontPreview ? (
              <img
                src={formData.idFrontPreview}
                alt="ID Front Preview"
                className="object-cover w-full h-40 rounded-lg"
              />
            ) : (
              <>
                <FileText size={36} className="mb-2 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">ID Front</p>
                <p className="mt-1 text-xs text-gray-500">Click to upload</p>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0], "front")}
            />
          </motion.label>

          {/* Back */}
          <motion.label
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="flex overflow-hidden relative flex-col justify-center items-center p-4 text-center rounded-xl border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-emerald-400 bg-white/80"
          >
            {formData.idBackPreview ? (
              <img
                src={formData.idBackPreview}
                alt="ID Back Preview"
                className="object-cover w-full h-40 rounded-lg"
              />
            ) : (
              <>
                <FileText size={36} className="mb-2 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">ID Back</p>
                <p className="mt-1 text-xs text-gray-500">Click to upload</p>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0], "back")}
            />
          </motion.label>
        </motion.div>
      )}
      {/* Date Fields - only active when document type is selected */}
      {formData.document_type && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {/* Issue Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Date of Issue
            </label>
            <InputField
              type="date"
              placeholder=""
              value={formData.issue_date}
              onChange={(e) => onChange("issue_date", e.target.value)}
              delay={0.4}
              disabled={!formData.document_type}
              error={getFieldError("issue_date")}
            />
          </div>
          <div className="space-y-2">
            {/* Expiry Date */}
            <label className="text-sm font-medium text-gray-700">
              Expiration Date
            </label>
            <InputField
              type="date"
              placeholder=""
              value={formData.expiry_date}
              onChange={(e) => onChange("expiry_date", e.target.value)}
              delay={0.45}
              disabled={!formData.document_type}
              error={getFieldError("expiry_date")}
            />
          </div>
        </motion.div>
      )}
    </div>
  );
}

// Step 3: Security Setup
function SecuritySetupStep({ formData, onChange, getFieldError }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "gray" };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    score += checks.length ? 1 : 0;
    score += checks.lowercase ? 1 : 0;
    score += checks.uppercase ? 1 : 0;
    score += checks.numbers ? 1 : 0;
    score += checks.symbols ? 1 : 0;

    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;

    let label, color;
    if (score <= 2) {
      label = "Very Weak";
      color = "red";
    } else if (score <= 3) {
      label = "Weak";
      color = "orange";
    } else if (score <= 4) {
      label = "Fair";
      color = "yellow";
    } else if (score <= 5) {
      label = "Good";
      color = "lime";
    } else if (score <= 6) {
      label = "Strong";
      color = "green";
    } else {
      label = "Very Strong";
      color = "emerald";
    }

    return { score, label, color, checks };
  };

  const passwordStrength = calculatePasswordStrength(formData.password);
  const strengthColors = {
    red: "bg-red-500",
    orange: "bg-orange-500",
    yellow: "bg-yellow-500",
    lime: "bg-lime-500",
    green: "bg-green-500",
    emerald: "bg-emerald-500",
  };

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <h3 className="text-lg font-semibold">Security Setup</h3>
        <p className="text-xs text-gray-500">
          Create a secure password for your account
        </p>
      </motion.div>

      <div className="space-y-3">
        <div>
          <div className="relative">
            <InputField
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={formData.password}
              onChange={(e) => onChange("password", e.target.value)}
              delay={0.2}
              error={getFieldError("password")}
            />

            {/* Password Visibility Toggle */}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 p-1 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Password Strength Indicator */}
          {formData.password && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              className="mt-2 space-y-2"
            >
              {/* Strength Bar */}
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">Password strength:</span>
                  <span
                    className={`font-medium ${
                      passwordStrength.color === "red"
                        ? "text-red-600"
                        : passwordStrength.color === "orange"
                        ? "text-orange-600"
                        : passwordStrength.color === "yellow"
                        ? "text-yellow-600"
                        : passwordStrength.color === "lime"
                        ? "text-lime-600"
                        : passwordStrength.color === "green"
                        ? "text-green-600"
                        : "text-emerald-600"
                    }`}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      strengthColors[passwordStrength.color]
                    }`}
                    style={{ width: `${(passwordStrength.score / 7) * 100}%` }}
                  />
                </div>
              </div>

              {/* Requirements Checklist */}
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div
                  className={`flex items-center gap-2 ${
                    passwordStrength.checks.length
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      passwordStrength.checks.length
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  At least 8 characters
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    passwordStrength.checks.lowercase
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      passwordStrength.checks.lowercase
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Lowercase letter
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    passwordStrength.checks.uppercase
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      passwordStrength.checks.uppercase
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Uppercase letter
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    passwordStrength.checks.numbers
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      passwordStrength.checks.numbers
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Number
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    passwordStrength.checks.symbols
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      passwordStrength.checks.symbols
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Special character
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    formData.password.length >= 12
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      formData.password.length >= 12
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  12+ characters
                </div>
              </div>
            </motion.div>
          )}
        </div>

        <div className="relative">
          <InputField
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={(e) => onChange("confirmPassword", e.target.value)}
            delay={0.25}
            error={getFieldError("password_confirmation")}
          />

          {/* Confirm Password Visibility Toggle */}
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 p-1 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
            title={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {formData.confirmPassword && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`flex items-center gap-2 text-xs ${
              formData.password === formData.confirmPassword
                ? "text-emerald-600"
                : "text-red-500"
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${
                formData.password === formData.confirmPassword
                  ? "bg-emerald-500"
                  : "bg-red-500"
              }`}
            />
            {formData.password === formData.confirmPassword
              ? "Passwords match"
              : "Passwords don't match"}
          </motion.div>
        )}

        {/* Terms and Conditions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="flex items-start gap-3 pt-2"
        >
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={formData.acceptedTerms || false}
              onChange={(e) => onChange("acceptedTerms", e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-2 accent-emerald-500 cursor-pointer"
            />
            <span className="text-sm text-gray-600">
              I have read and accepted the{" "}
              <Link
                to="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 font-medium underline underline-offset-2 hover:text-emerald-700"
              >
                Terms and Conditions
              </Link>
            </span>
          </label>
        </motion.div>
      </div>
    </div>
  );
}

// Reusable Input Field Component
function InputField({
  type,
  placeholder,
  value,
  onChange,
  delay = 0,
  disabled = false,
  error = null,
  fieldName = "",
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <div
        className={`rounded-xl border transition-all duration-300 ${
          disabled
            ? "bg-gray-100 border-gray-200"
            : error
            ? "border-red-500 bg-white/95 group-hover:border-red-400 group-focus-within:border-red-600 group-focus-within:ring-2 group-focus-within:ring-red-100"
            : "border-gray-200 bg-white/95 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100"
        }`}
      >
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-3 text-sm font-medium bg-transparent rounded-[0.95rem] outline-none transition-all duration-300 ${
            disabled
              ? "text-gray-400 cursor-not-allowed placeholder:text-gray-300"
              : "placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900 focus:outline-none focus:ring-0 invalid:shadow-none invalid:outline-none invalid:ring-0"
          }`}
        />
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}

const PhoneInputStyledInput = forwardRef(function PhoneInputStyledInput(
  { className, ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      {...rest}
      className={`w-full bg-transparent px-0 py-0 text-sm font-medium outline-none transition-all duration-300 placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900 focus:outline-none focus:ring-0 invalid:shadow-none invalid:outline-none invalid:ring-0 ${
        className || ""
      }`}
    />
  );
});

// Phone Number Field Component (international)
function PhoneNumberField({ value, onChange, delay = 0, error = null }) {
  const phoneInputValue = value ? `+${value}` : undefined;

  const handleChange = (newValue) => {
    // react-phone-number-input returns E.164 with a leading "+" (example: "+1213...")
    // Backend expects digits-only (example: "1213...").
    const normalized = newValue
      ? newValue.replace(/^\+/, "").replace(/\D/g, "")
      : "";
    onChange(normalized);
  };

  const containerComponent = useMemo(
    () =>
      ({ className, style, children, ...rest }) => (
        <div
          {...rest}
          className={`${className || ""} rounded-xl border transition-all duration-300 px-4 py-3 text-gray-600 ${
            error
              ? "border-red-500 bg-white/95 group-hover:border-red-400 group-focus-within:border-red-600 group-focus-within:ring-2 group-focus-within:ring-red-100"
              : "border-gray-200 bg-white/95 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100"
          }`}
          style={{
            ...style,
            // Align icon/arrow styling with the same border colors.
            "--PhoneInput-color--focus": error ? "#dc2626" : "#059669",
            "--PhoneInputCountryFlag-borderColor": error
              ? "rgba(239, 68, 68, 1)"
              : "rgba(229, 231, 235, 1)",
            "--PhoneInputCountryFlag-borderColor--focus": error
              ? "#dc2626"
              : "#059669",
            "--PhoneInputCountryFlag-backgroundColor--loading":
              "rgba(255, 255, 255, 0)",
          }}
        >
          {children}
        </div>
      ),
    [error]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <PhoneInput
        international
        defaultCountry="GH"
        placeholder="Enter phone number"
        value={phoneInputValue}
        onChange={handleChange}
      containerComponent={containerComponent}
      inputComponent={PhoneInputStyledInput}
      />
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}

// Reusable Select Field Component with Search
function SelectField({
  placeholder,
  value,
  onChange,
  options,
  delay = 0,
  searchable = false,
  disabled = false,
  allowClear = false,
  onClear,
  error = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleOptionClick = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <div
        className={`rounded-xl border transition-all duration-300 bg-white/95 ${
          disabled
            ? "bg-gray-100 border-gray-200"
            : error
            ? "border-red-500 group-hover:border-red-400 group-focus-within:border-red-600 group-focus-within:ring-2 group-focus-within:ring-red-100"
            : "border-gray-200 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100"
        }`}
      >
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={disabled}
            className={`flex-1 px-4 py-3 text-sm font-medium bg-transparent rounded-[0.95rem] outline-none transition-all duration-300 text-left ${
              disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer"
            }`}
          >
            <span
              className={value ? "text-gray-900" : "font-normal text-gray-400"}
            >
              {value || placeholder}
            </span>
          </button>

          <div className="flex gap-2 items-center pr-4">
            {allowClear && value && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear?.();
                }}
                className="p-1 text-gray-400 transition-colors hover:text-gray-600"
                title="Clear selection"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              } ${disabled ? "text-gray-400" : ""}`}
            />
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: -8, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden absolute bottom-full z-50 py-1 mb-2 w-full max-h-60 bg-white rounded-xl border border-gray-200 shadow-lg"
            >
              {/* Search Input */}
              {searchable && (
                <div className="px-3 py-2 border-b border-gray-100">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-2 top-1/2 text-gray-400 transform -translate-y-1/2"
                    />
                    <input
                      type="text"
                      placeholder="Search options..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="py-2 pr-3 pl-8 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* Options List */}
              <div className="overflow-y-auto max-h-48">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleOptionClick(option)}
                      className="px-4 py-2 w-full text-sm text-left transition-colors hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none"
                    >
                      {option}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-center text-gray-500">
                    No options found
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}
